import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowPathIcon,
  ClipboardDocumentListIcon,
} from "@heroicons/react/24/outline";
import { ReportRerunSummary, ReportSummaryItemApi } from "../api/Reports";
import {
  formatRerunReportLinkDate,
  getOtherRerunReports,
  getRerunParentReportUuid,
} from "../reports/reportRerunUtils";

export type RerunReportBannerVariant =
  | "summary"
  | "workbench"
  | "misconduct-summary";

export type RerunReportBannerReportType = "360" | "misconduct";

interface Props {
  report: ReportSummaryItemApi;
  reportReruns: ReportRerunSummary[] | undefined;
  variant: RerunReportBannerVariant;
  reportType?: RerunReportBannerReportType;
}

function buildReportSummaryUrl(
  reportType: RerunReportBannerReportType,
  reportUuid: string
): string {
  return reportType === "360"
    ? `/360/${reportUuid}/summary`
    : `/reports/${reportUuid}/summary`;
}

const VARIANT_STYLES: Record<
  RerunReportBannerVariant,
  {
    container: string;
    link: string;
    icon: string;
    Icon: typeof ClipboardDocumentListIcon;
    includeYearInDates: boolean;
    trailingPeriod: boolean;
    hideInPrint: boolean;
  }
> = {
  summary: {
    container:
      "flex rounded-lg border border-green-600 bg-white mt-2 mb-5 text-sm text-gray-600 p-3 items-center gap-3 print:hidden",
    link: "text-sky-500 underline font-medium",
    icon: "text-gray-500 w-6 shrink-0",
    Icon: ClipboardDocumentListIcon,
    includeYearInDates: true,
    trailingPeriod: true,
    hideInPrint: true,
  },
  workbench: {
    container:
      "flex rounded-lg border border-blue-200 bg-blue-50 mb-4 text-sm text-gray-800 p-3 items-center gap-3 print:hidden",
    link: "text-blue-600 no-underline font-medium",
    icon: "text-slate-400 w-6 shrink-0",
    Icon: ArrowPathIcon,
    includeYearInDates: false,
    trailingPeriod: false,
    hideInPrint: true,
  },
  "misconduct-summary": {
    container:
      "flex rounded-lg border border-blue-300 bg-blue-50 mt-2 mb-5 text-sm text-gray-800 p-3 items-center gap-3",
    link: "text-blue-600 font-medium underline",
    icon: "text-blue-600 w-6 shrink-0",
    Icon: ArrowPathIcon,
    includeYearInDates: false,
    trailingPeriod: false,
    hideInPrint: false,
  },
};

export function RerunReportBanner({
  report,
  reportReruns,
  variant,
  reportType = "360",
}: Props) {
  const rerunParentUuid = getRerunParentReportUuid(report, reportReruns);
  if (!rerunParentUuid) return null;

  const {
    container,
    link,
    icon,
    Icon,
    includeYearInDates,
    trailingPeriod,
    hideInPrint,
  } = VARIANT_STYLES[variant];
  const containerClassName = hideInPrint
    ? container
    : container.replace(" print:hidden", "");
  const otherVersions = getOtherRerunReports(report.report_uuid, reportReruns);
  const versionsToLink =
    otherVersions.length > 0
      ? otherVersions
      : reportReruns?.filter((r) => r.report_uuid === rerunParentUuid) ?? [];

  const formatLinkDate = (finishedAt: string) =>
    formatRerunReportLinkDate(finishedAt, {
      includeYear: includeYearInDates,
    });

  if (versionsToLink.length === 0) {
    return (
      <div className={containerClassName} role="status">
        <Icon className={icon} aria-hidden />
        <span>
          This report is a rerun of a previously completed report.{" "}
          <Link
            to={buildReportSummaryUrl(reportType, rerunParentUuid)}
            target="_blank"
            rel="noopener noreferrer"
            className={link}
          >
            View previous report
          </Link>
          {trailingPeriod ? "." : null}
        </span>
      </div>
    );
  }

  return (
    <div className={containerClassName} role="status">
      <Icon className={icon} aria-hidden />
      <span>
        This report is a rerun of a previously completed report. View other
        versions of the reports ran on{" "}
        {versionsToLink.map((version, index) => (
          <React.Fragment key={version.report_uuid}>
            {index > 0 &&
              (index === versionsToLink.length - 1 ? " and " : ", ")}
            <Link
              to={buildReportSummaryUrl(reportType, version.report_uuid)}
              target="_blank"
              rel="noopener noreferrer"
              className={link}
            >
              {formatLinkDate(version.finished_at)}
            </Link>
          </React.Fragment>
        ))}
        {trailingPeriod ? "." : null}
      </span>
    </div>
  );
}

export default RerunReportBanner;
